import { apiController } from '@A/controllers/controller.api'
import type { OrganizationType } from '@/shared/types/organization.type'
import type { AccessTier } from '§§C/types/access-tier.type'
import type { AssistantAccess } from '§§C/constants/ai-api-key'

type CurrentUserPermissionsResponse = {
  permissions: string[]
  access_tier: AccessTier
  organization_permissions: string[]
  assistant: AssistantAccess
  organizations: OrganizationType[]
}

type UserRole = {
  uuid: string
  title: string
  common_id: string
  description?: string
  is_system_role: boolean
  context_scope: string
}

type CurrentUserRolesResponse = {
  roles: UserRole[]
}

/**
 * Access Control API Controller
 *
 * Provides HTTP client methods for access control operations.
 * Used to fetch current user's permissions and roles.
 */
export const apiAccessControl = () => {
  const { controllerGet } = apiController('/access-control')

  /**
   * Gets the current user's permissions, roles flags, and accessible organizations.
   *
   * @returns Promise resolving to the current user's permission state
   */
  const getCurrentUserPermissions = async (organizationId?: string): Promise<CurrentUserPermissionsResponse> => {
    const path = organizationId ? `me/permissions?organizationId=${organizationId}` : 'me/permissions'
    return await controllerGet<CurrentUserPermissionsResponse>(path)
  }

  /**
   * Gets the current user's roles.
   *
   * @returns Promise resolving to the current user's roles
   */
  const getCurrentUserRoles = async (): Promise<CurrentUserRolesResponse> => {
    return await controllerGet<CurrentUserRolesResponse>('me/roles')
  }

  return {
    getCurrentUserPermissions,
    getCurrentUserRoles
  }
}
